
import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface DateSelectorProps {
  selectedDate: { month: number; year: number };
  onDateChange: (date: { month: number; year: number }) => void;
}

export const DateSelector: React.FC<DateSelectorProps> = ({
  selectedDate,
  onDateChange
}) => {
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear + i);

  const handleMonthChange = (monthStr: string) => {
    const month = parseInt(monthStr);
    onDateChange({ ...selectedDate, month });
  };

  const handleYearChange = (yearStr: string) => {
    const year = parseInt(yearStr);
    onDateChange({ ...selectedDate, year });
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    let newMonth = selectedDate.month;
    let newYear = selectedDate.year;

    if (direction === 'prev') {
      newMonth--;
      if (newMonth < 0) {
        newMonth = 11;
        newYear--;
      }
    } else {
      newMonth++;
      if (newMonth > 11) {
        newMonth = 0;
        newYear++;
      }
    }

    onDateChange({ month: newMonth, year: newYear });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Calendar className="w-5 h-5 text-gold" />
        <h3 className="text-lg font-semibold text-foreground">Select Month & Year</h3>
      </div>

      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigateMonth('prev')}
          className="p-2"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        <div className="flex gap-2 flex-1">
          <Select value={selectedDate.month.toString()} onValueChange={handleMonthChange}>
            <SelectTrigger className="flex-1 bg-background text-foreground border-border font-medium">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-background border-2 border-border shadow-xl text-foreground z-50">
              {monthNames.map((month, index) => (
                <SelectItem
                  key={index}
                  value={index.toString()}
                  className="text-foreground hover:bg-muted focus:bg-muted font-medium cursor-pointer"
                >
                  {month}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedDate.year.toString()} onValueChange={handleYearChange}>
            <SelectTrigger className="w-24 bg-background text-foreground border-border font-medium">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-background border-2 border-border shadow-xl text-foreground z-50">
              {years.map((year) => (
                <SelectItem
                  key={year}
                  value={year.toString()}
                  className="text-foreground hover:bg-muted focus:bg-muted font-medium cursor-pointer"
                >
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => navigateMonth('next')}
          className="p-2"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      <div className="text-center">
        <p className="text-sm text-muted-foreground">
          Selected: <span className="font-medium text-foreground">
            {monthNames[selectedDate.month]} {selectedDate.year}
          </span>
        </p>
      </div>
    </div>
  );
};
